import styles from './MarginTitleGrid.module.css';
import Arrow from '../assets/arrowTopRight.jsx';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { ReactCompareSlider, ReactCompareSliderImage } from 'react-compare-slider';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import CMSLink from './common/CMSLink.jsx';

gsap.registerPlugin(useGSAP, ScrollTrigger, SplitText);

function MarginTitleGrid({ title, items, projectIntro }) {

    // get the current lang for routing
    const { lang } = useParams();

    const sectionEl = useRef();
    const titleEl = useRef();
    const contentEl = useRef();
    const textBlockEl = useRef();
    const splitInstance = useRef(null);

    useEffect(() => {

        if (splitInstance.current) {

            splitInstance.current.revert();  // <- This puts the DOM back to normal
            splitInstance.current = null;
        }

        if (typeof textBlockEl.current != "undefined" && textBlockEl.current != null) {

            // SplitText text reveal by line for textBlock only element
            gsap.set(textBlockEl.current, { opacity: 0 });

            setTimeout(function () {

                splitInstance.current = new SplitText(textBlockEl.current.querySelectorAll('p:not(li p), ul'), {
                    type: "lines",
                    linesClass: "line"
                });

                gsap.set(textBlockEl.current, { opacity: 1 });

                gsap.from(splitInstance.current.lines, {
                    scrollTrigger: {
                        trigger: textBlockEl.current,
                        start: "top 80%",
                        toggleActions: "play reverse play reverse",
                        markers: false,
                    },
                    duration: .7,
                    yPercent: 100,
                    opacity: 0,
                    ease: "power2.out",
                    stagger: 0.1
                });

            }, 500);

        }

        return () => {
            if (splitInstance.current) {
                splitInstance.current.revert();
                splitInstance.current = null;
            }
        };

    }, [items]);

    useGSAP(() => {



        // pinning the left side titles only if not mobile view
        if (
            window.innerWidth > 768 &&
            !window.matchMedia('(orientation: landscape) and (max-height: 520px)').matches
        ) {
            ScrollTrigger.create({
                trigger: sectionEl.current,
                start: "top center+=50",
                end: () => `+=${contentEl.current.offsetHeight - 50}`,
                pin: titleEl.current,
                pinSpacing: false,
                markers: false,
            });
        }

        // adding a refresh here in case content changes height of page, ideally all content would have wrappers with aspect ratio
        setTimeout(function () {
            ScrollTrigger.refresh();
        }, 500);
    });

    return (
        <div className={`${styles.project_grid} ${styles.margin_title_grid} ${lang === "ko" && styles.lang_ko}`} ref={sectionEl}>

            <div className={`${styles.section_title} preserve-line-breaks`} ref={titleEl}>
                <h2>{title}</h2>
            </div>

            <div className={styles.grid} ref={contentEl}>

                {/* Map through Items - Margin Grid Blocks to figure out which module to use  */}
                {items.map((section, i) => {

                    // -------------Check if the layout is Three Column Projects -------------
                    if (typeof section.fields.threeColumnProjects !== 'undefined') {
                        // Uses Item - Projects
                        let projects = section.fields.threeColumnProjects;
                        // Item size is going to be large by default for this layout
                        let itemSize = styles.large;

                        return (

                            <div className={`${itemSize} ${styles.cell} ${styles.three_col} ${section.fields.cropMedia ? styles.crop : ''}`} key={section.sys.id}>
                                {projects.map((item, i) => (

                                    <a href={`/${lang}/projects/${item.fields.slug}`} key={item.sys.id}>
                                        <div className={styles.media}>
                                            <img
                                                src={`https:${item.fields.gridImage.fields.file.url}`}
                                                alt={item.fields.title}
                                                width={item.fields.gridImage.fields.file.details.image.width}
                                                height={item.fields.gridImage.fields.file.details.image.height}
                                                className="absolute-cover"
                                            />

                                        </div>

                                        <div className={styles.info}>
                                            <h2 className={styles.title}>{item.fields.title}</h2>
                                        </div>

                                    </a>


                                ))}
                            </div>
                        )
                    } else if (typeof section.fields.moreProjects !== 'undefined') {

                        return (

                            <div className={styles.moreGrid} key={section.sys.id}>

                                {section.fields.moreProjects.map((item, i) => (

                                    <div className={`${styles.cell} ${styles.regular}`} key={`${section.sys.id}_${i}`}>
                                        
                                            <a href={`/${lang}/projects/${item.fields.slug}`}>
                                                <div className={styles.media}>
                                                    <img
                                                        src={`https:${item.fields.gridImage.fields.file.url}`}
                                                        alt={item.fields.title}
                                                        width={item.fields.gridImage.fields.file.details.image.width}
                                                        height={item.fields.gridImage.fields.file.details.image.height}
                                                        className="absolute-cover"
                                                    />

                                                </div>

                                                <div className={styles.info}>
                                                    <h2 className={styles.title}>{item.fields.title}</h2>
                                                </div>

                                            </a>

                                    </div>

                                ))}

                            </div>
                        )

                    // -------------Check if the layout is Three Column Text -------------
                    } else if (typeof section.fields.threeColumnText !== 'undefined') {
                        // Item size is going to be large by default for this layout
                        let itemSize = styles.large;
                        let textItems = section.fields.threeColumnText;
                        // Uses Item - Content
                        return (

                            <div className={`${itemSize} ${styles.cell} ${styles.three_col} ${styles.three_col_text}`} key={section.sys.id}>
                                {textItems.map((item, i) => {

                                    return (


                                        <div className={`${styles.caption} preserve-line-breaks`} key={item.sys.id}>
                                            <h3> {item.fields.caption} </h3>
                                            <h2 className={styles.title}> {item.fields.title} </h2>
                                            <div className={styles.summary}>
                                                {documentToReactComponents(item.fields.summary)}
                                            </div>
                                            <CMSLink path={item.fields.ctaLink} icon="arrow">
                                                {item.fields.ctaText}
                                            </CMSLink>
                                        </div>

                                    );

                                })}
                            </div>
                        )

                    // -------------Check if the layout is Comparison Slider  -------------

                    } else if (typeof section.fields.awards !== 'undefined') {

                        let awards = section.fields.awards;


                        return (
                            <div className={styles.awards}>

                                {awards.map((award, i) => {

                                    return (
                                        <div className={styles.award} key={award.sys.id}>
                                            <div className={styles.icon}>
                                                <img
                                                    src={`https:${award.fields.image.fields.file.url}`}
                                                    alt={award.fields.title}
                                                    width={award.fields.image.fields.file.details.image.width}
                                                    height={award.fields.image.fields.file.details.image.height}
                                                    />
                                            </div>
                                            <div className={styles.info}>
                                                <p className={styles.subtitle}>{award.fields.subtitle}</p>
                                                <p className={styles.title}>{award.fields.title}</p>
                                                <p className={`${styles.categories} preserve-line-breaks`}>{award.fields.categories}</p>
                                            </div>
                                            
                                        </div>
                                    )

                                })}

                            </div>
                        )


                    }

                    // -------------Check if the layout is Comparison Slider  -------------

                    else if (typeof section.fields.comparisonSlider !== 'undefined') {

                        // Item size is going to be large by default for this layout
                        let itemSize = styles.large;

                        let itemOneURL = section.fields.comparisonSlider[0].fields.file.url;
                        let itemTwoURL = section.fields.comparisonSlider[1].fields.file.url;
                        let itemOneSrcSet = `
                            ${itemOneURL}?w=500 500w, 
                            ${itemOneURL}?w=1000 1000w, 
                            ${itemOneURL}?w=2000 2000w`
                        let itemTwoSrcSet = `
                            ${itemTwoURL}?w=500 500w, 
                            ${itemTwoURL}?w=1000 1000w, 
                            ${itemTwoURL}?w=2000 2000w`



                        // Uses Item - Content
                        return (

                            <div className={`${itemSize} ${styles.cell} ${styles.three_col}`} key={section.sys.id}>
                                <ReactCompareSlider
                                    itemOne={<ReactCompareSliderImage src={`${itemOneURL}?w=1000`} srcSet={itemOneSrcSet} alt="Image one" />}
                                    itemTwo={<ReactCompareSliderImage src={`${itemTwoURL}?w=1000`} srcSet={itemTwoSrcSet} alt="Image two" />}
                                    buttonstyle={{ display: 'none' }}

                                />
                            </div>
                        )

                    }

                    // ------------- If neither, render DEFAULT block -------------

                    else {

                        // Check if we are going to use the text_only class for the caption
                        let textClass = `${styles.caption}`;
                        if (!section.fields.gridImage) {
                            textClass += ` ${styles.text_only}`;
                        }
                        if (section.fields.hideMargin) {
                            textClass += ` ${styles.no_margin}`;
                        }

                        // Item size will be determined by the chosen option in Contentful for the gridSize field
                        let itemSize = section.fields.gridSize === "large" ? styles.large : styles.regular;

                        const getMediaType = (url) => {
                            if (!url) return null;
                            const extension = url.split('.').pop().toLowerCase();
                            const videoExtensions = ['mp4', 'webm', 'ogg', 'mov'];
                            const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

                            if (videoExtensions.includes(extension)) return 'video';
                            if (imageExtensions.includes(extension)) return 'image';
                            return null;
                        };

                        const mediaType = getMediaType(section.fields.gridImage?.fields.file.url);

                        // Check if item size is large and if there are example projects to render within the large item
                        if (itemSize === styles.large && typeof section.fields.exampleProjects !== 'undefined') {

                        }

                        return (

                            <div className={`${styles.cell} ${itemSize}`} key={`${lang}_${i}`}>
                                {section.fields.gridImage ? (<div>
                                    <div className={styles.media}>
                                        {section.fields.gridImage && mediaType === "video" && (
                                            <video className={`${styles.video} `} autoPlay loop muted playsInline>
                                                <source src={section.fields.gridImage.fields.file.url} type="video/mp4" />
                                            </video>
                                        )}

                                        {section.fields.gridImage && mediaType === "image" && (
                                            <img
                                                // className="absolute-cover"
                                                src={section.fields.gridImage.fields.file.url}
                                                alt={section.fields.gridImage.fields.title}
                                                width={section.fields.gridImage.fields.file.details.image.width}
                                                height={section.fields.gridImage.fields.file.details.image.height}

                                            />
                                        )}


                                    </div>

                                    {section.fields.textContent && (

                                        <div className={`${styles.caption} preserve-line-breaks`}>
                                            <h2 className={styles.title}>{section.fields.title}</h2>
                                            {/* This is labeled as Text Content in Contentful */}

                                            {documentToReactComponents(section.fields.textContent)}
                                            {section.fields.link && (
                                                <div className={styles.cta}>
                                                    <a href={`/${lang}${section.fields.link}`}>
                                                        {section.fields.linkText}
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                                                            <path d="M1.81836 8.33341L8.70283 1.66675M8.70283 1.66675L1.81836 1.66675M8.70283 1.66675V8.33341" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </a>
                                                </div>
                                            )}
                                        </div>

                                    )}


                                </div>) : (
                                    // Use Text Only Styles if there is no grid image for the item
                                    <div ref={textBlockEl} className={`${textClass} ${projectIntro && styles.project_intro} preserve-line-breaks`}>

                                        {section.fields.title && (

                                            <h2 className={styles.title}>{section.fields.title}</h2>

                                        )}

                                        {documentToReactComponents(section.fields.textContent)}

                                        {section.fields.link && (
                                            <CMSLink path={section.fields.ctaLink} icon="arrow">
                                                {section.fields.ctaText}
                                            </CMSLink>
                                        )}
                                    </div>)
                                }



                            </div>


                        )
                    }


                }

                )}

            </div>

        </div>
    )
}

export default MarginTitleGrid;